import { BREAKPOINTS_V3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { CarouselImage } from '@/app/[locale]/product/_types/carouselTypes';
import Image from '@/app/components/Image';
import Video from '@/app/components/Video/Video';
import type { Products_content_listProducts_wizard_sizeStep_carouselImages_content } from '@/queries/types/Products';

import SlideshowImageContentItem from './SlideshowImageContentItem';
import SlideshowOR4TechSpecsContent from './SlideshowOR4TechSpecsContent';

const AWS_BASE_URL = 'https://s3.amazonaws.com/ouraring.com/images/';

interface SlideshowItemProps extends CarouselImage {
  readonly activeSlide: number;
  readonly showDiscount: boolean;
  readonly idx: number;
}

type ContentProps =
  Products_content_listProducts_wizard_sizeStep_carouselImages_content | null;

// helper fn to consistently determine whether a file is a video
const VIDEO_EXTENSIONS = ['.mp4'];
const isVideo = (src: string | undefined) =>
  VIDEO_EXTENSIONS.some((ext) => src?.includes(ext));

const videoProps = {
  autoPlay: true,
  contentTitle: 'pdp_video',
  controls: false,
  location: 'styleStep',
  loop: true,
  muted: true,
  playsInline: true,
  type: 'video/mp4',
};

const SlideshowItem = ({
  activeSlide,
  altMsgKey,
  content,
  showDiscount,
  idx,
  mobileSrc,
  originalSrc,
  originalSrcKey,
  thumbnailSrc,
}: SlideshowItemProps): JSX.Element | null => {
  const t = useTranslations();

  const isLargeScreen = `(min-width: ${BREAKPOINTS_V3.large}px)`;

  const src = originalSrcKey ? t(originalSrcKey) : originalSrc;
  const imageWidth = !isLargeScreen ? { width: 1024 } : {};
  const imageHeight = !isLargeScreen ? { height: 868 } : {};
  const _mobileVideoSrc = mobileSrc?.includes(AWS_BASE_URL)
    ? mobileSrc.split(AWS_BASE_URL)[1]
    : mobileSrc;
  const _desktopVideoSrc = src.includes(AWS_BASE_URL)
    ? src.split(AWS_BASE_URL)[1]
    : src;

  return (
    <div
      className={cx(
        'pointer-events-none h-full max-h-[60vh] overflow-hidden',
        'lg:flex lg:h-screen lg:max-h-full lg:justify-center',
        {
          'visibility-hidden opacity-0 transition-opacity duration-100':
            activeSlide !== idx,
          'duration-750 visibility-visible opacity-100 transition-opacity':
            activeSlide === idx,
        },
      )}
      data-active={activeSlide === idx}
      data-cy={`pdp-slideshow-img-${idx}`}
    >
      {isVideo(src) ? (
        <Video
          {...videoProps}
          altImage={{
            priority: idx === 0,
            src: thumbnailSrc!,
            width: 609,
            height: 457,
          }}
          className="size-full max-h-[60vh] object-cover lg:h-auto lg:max-h-full"
          playVideo={activeSlide === idx}
          srcSet={[
            {
              src: _desktopVideoSrc,
              media: isLargeScreen,
            },
            { src: _mobileVideoSrc as string | undefined },
          ]}
        />
      ) : (
        <Image
          className="object-cover"
          draggable="false"
          // PDP wizard uses a different Layout for large screens, so it needs a different Image setup
          fill={Boolean(isLargeScreen)}
          localizedAlt={altMsgKey as MessageKey}
          priority={idx === 0}
          quality={65}
          sizes="(min-width: 1440px) 100vw, (min-width: 600px) 1024px, (min-width: 400px) 600px, 400px"
          src={src}
          {...imageWidth}
          {...imageHeight}
        />
      )}
      {Array.isArray(content) && (
        <div
          className={cx('absolute left-6 lg:left-28 lg:top-32', {
            'top-24 md:top-28': showDiscount,
            'top-16': !showDiscount,
          })}
        >
          {content.map((contentProps: ContentProps) => {
            if (!contentProps) return null;

            return (
              <SlideshowImageContentItem
                key={contentProps.heading}
                {...contentProps}
              />
            );
          })}
        </div>
      )}
      {content?.title && (
        <SlideshowOR4TechSpecsContent
          showDiscount={showDiscount}
          {...content}
        />
      )}
    </div>
  );
};

export default SlideshowItem;
